'use client'

import React, { useState } from 'react'

import { Box, Text, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { Button } from '../Button'
import { CheckIcon } from '../Icon/CheckIcon'
import { ThumbsDownIcon } from '../Icon/ThumbsDownIcon'
import { ThumbsUpIcon } from '../Icon/ThumbsUpIcon'
import { MultilineTextInput } from '../MultilineTextInput'
import {
  containerStyles,
  expandInnerStyles,
  expandWrapperStyles,
  questionStyles,
  recordedLabelStyles,
  recordedStyles,
  submitRowStyles,
  thumbButtonStyles,
  thumbRowStyles,
} from './Feedback.styles'

/** Server-enforced comment bound (GraphQL `@Pattern` `^[\s\S]{1,1000}$`). */
const MAX_COMMENT_LENGTH = 1000

export type FeedbackProps = {
  colorScheme?: ColorScheme
  /** Prompt copy, e.g. "Was this assessment helpful?". */
  question: string
  /** Current thumb selection: `true` up, `false` down, `null`/undefined neutral. */
  selected?: boolean | null
  commentValue: string
  onCommentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  onThumbClick: (isPositive: boolean) => void
  onSubmitComment: () => void
  isSubmitting?: boolean
  commentPlaceholder?: string
  submitLabel: string
  /** Accessible label for the positive (up) thumb. */
  positiveAriaLabel?: string
  /** Accessible label for the negative (down) thumb. */
  negativeAriaLabel?: string
  /** Max comment length. Defaults to the server-enforced 1000. */
  maxCommentLength?: number
  /** Show the "feedback recorded" confirmation (after a successful submit). */
  showRecordedConfirmation?: boolean
  /** Confirmation copy, e.g. "Feedback recorded". */
  recordedLabel?: string
  /**
   * Render expanded (comment + submit visible) from the start. Defaults to
   * collapsed — the widget expands once a thumb is clicked.
   */
  defaultExpanded?: boolean
}

/**
 * AI Operator feedback widget — presentational only. Renders a prompt with
 * reversible thumbs up/down and an optional comment + submit. Fully controlled:
 * the host owns selection/comment state and submission (a thumb click and a
 * comment submit are separate writes). Build on `@hivewatch/dark` primitives so
 * both color schemes stay correct; the host wraps this in `DarkProvider` and
 * supplies its own top divider (e.g. the ReasoningTrail card footer).
 */
export const Feedback = ({
  colorScheme = 'dark',
  question,
  selected,
  commentValue,
  onCommentChange,
  onThumbClick,
  onSubmitComment,
  isSubmitting = false,
  commentPlaceholder,
  submitLabel,
  positiveAriaLabel = 'Yes, this was helpful',
  negativeAriaLabel = 'No, this was not helpful',
  maxCommentLength = MAX_COMMENT_LENGTH,
  showRecordedConfirmation = false,
  recordedLabel,
  defaultExpanded = false,
}: FeedbackProps) => {
  // Starts collapsed (thumbs only); the comment + submit reveal once the user
  // picks a thumb. One-way — there is no collapse affordance in the design.
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const handleThumbClick = (isPositive: boolean) => {
    setIsExpanded(true)
    onThumbClick(isPositive)
  }

  // `isPositive` is required on submit, so a comment cannot be sent until a
  // thumb is chosen; an empty/whitespace comment is likewise blocked.
  const isSubmitDisabled =
    selected == null || commentValue.trim().length === 0 || isSubmitting

  return (
    <Box {...containerStyles}>
      <Box {...thumbRowStyles}>
        <Text {...questionStyles(colorScheme)}>{question}</Text>
        <chakra.button
          aria-label={positiveAriaLabel}
          aria-pressed={selected === true}
          disabled={isSubmitting}
          onClick={() => handleThumbClick(true)}
          type='button'
          {...thumbButtonStyles(colorScheme, selected === true)}
        >
          <ThumbsUpIcon size={15} />
        </chakra.button>
        <chakra.button
          aria-label={negativeAriaLabel}
          aria-pressed={selected === false}
          disabled={isSubmitting}
          onClick={() => handleThumbClick(false)}
          type='button'
          {...thumbButtonStyles(colorScheme, selected === false)}
        >
          <ThumbsDownIcon size={15} />
        </chakra.button>
      </Box>

      {/* Collapsible region: animates open when a thumb is picked. Kept
          mounted (not conditionally rendered) so the height/opacity can
          transition; `visibility` hides it from tab order + a11y while
          collapsed. */}
      <Box {...expandWrapperStyles(isExpanded)} aria-hidden={!isExpanded}>
        <Box {...expandInnerStyles(isExpanded)}>
          <MultilineTextInput
            colorScheme={colorScheme}
            fontSize='12px'
            isDisabled={isSubmitting}
            maxLength={maxCommentLength}
            onChange={onCommentChange}
            placeholder={commentPlaceholder}
            value={commentValue}
          />

          <Box {...submitRowStyles}>
            {/* Left slot: always present so space-between right-aligns the
                button; the confirmation only renders after a successful
                submit. */}
            <Box {...recordedStyles(colorScheme)}>
              {showRecordedConfirmation && recordedLabel && (
                <>
                  <CheckIcon size={16} />
                  <Text {...recordedLabelStyles(colorScheme)}>
                    {recordedLabel}
                  </Text>
                </>
              )}
            </Box>
            <Button
              colorScheme={colorScheme}
              disabled={isSubmitDisabled}
              isLoading={isSubmitting}
              onClick={onSubmitComment}
              size='md'
              variant='primary'
            >
              {submitLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
